import maplibregl from 'maplibre-gl';
import { type VehicleManager } from '../../managers/VehicleManager';
import { type Vehicle } from '../../model/Vehicle';
import { VehicleType } from '../../model/VehicleType';
import * as math from 'mathjs';
import { type ICustomLayer } from './ICustomLayer';
import { findClosestColorInAtlas, generateRainbowColors } from '../../tools/ColorTools';
import { type AtlasConfig } from '../../model/AtlasConfig';
import { TripService } from '../../services/TripService';
import { secondsToMinutes } from '../../tools/TimeRelated';
import { type PopupContent } from './VehiclePopup';

const tripService = new TripService();
type UpdatePopup = (visible: boolean, lngLat: [number, number], content?: PopupContent) => void;
export class VehicleCustomLayer implements ICustomLayer {
  id: string;
  type: 'custom';
  renderingMode: '2d';
  map!: maplibregl.Map;
  gl!: WebGL2RenderingContext;
  vehicleManager: VehicleManager;
  ready: boolean = false;
  clickedVehicle: Vehicle | undefined;
  popupContent?: PopupContent;

  // Shaders et programmes
  vertexShaderSource: string;
  fragmentShaderSource: string;
  program!: WebGLProgram;

  // Buffers
  positionBuffer!: WebGLBuffer;
  texCoordBuffer!: WebGLBuffer;
  vehicleTextureCoordBuffer!: WebGLBuffer;
  charCoordBuffer1!: WebGLBuffer;
  charCoordBuffer2!: WebGLBuffer;
  atlasConfigBuffer!: WebGLBuffer;
  typeBuffer!: WebGLBuffer;
  paintZoneBuffer!: WebGLBuffer;
  instanceCornerPositionBuffer!: WebGLBuffer;

  // Textures
  textureCars!: WebGLTexture;
  textureBuses!: WebGLTexture;
  textureTrams!: WebGLTexture;
  textureLetters!: WebGLTexture;
  textureUnitCars: number = 2;
  textureUnitBuses: number = 3;
  textureUnitTrams: number = 4;
  textureUnitLetters: number = 5;

  // Données des véhicules
  vehicles: Vehicle[] = [];
  instanceCornerPositions!: Float32Array;
  vehicleTypes!: Float32Array;
  vehicleTextureCoords!: Float32Array;
  charTextureCoords1!: Float32Array;
  charTextureCoords2!: Float32Array;
  paintZones!: Float32Array;
  atlasConfs!: Float32Array;

  // Variables pour la caméra
  eye_high!: Float32Array;
  eye_low!: Float32Array;
  cameraMoved: boolean = true;

  updatePopup: UpdatePopup;

  private readonly handleContextLost = (event: Event) => {
    // Empêche le comportement par défaut pour permettre la restauration
    console.warn('Contexte WebGL perdu pour VehicleCustomLayer. Libération des ressources.');
    this.ready = false;
    window.location.reload();
    // Optionnel : libérez ici les ressources si nécessaire.
  };

  private readonly handleContextRestored = (event: Event) => {
    console.log('Contexte WebGL restauré pour VehicleCustomLayer. Réinitialisation des ressources.');
    try {
      // Réinitialiser les ressources : shaders, buffers, textures, etc.
      this.initShaders();
      this.initBuffers();
      this.loadTextures();
      this.ready = true;
      // Forcer un repaint de la carte afin de réafficher la couche
      this.map.triggerRepaint();
    } catch (error) {
      console.error('Erreur lors de la réinitialisation après restauration du contexte :', error);
    }
  };

  constructor(vehicleManager: VehicleManager, updatePopup: UpdatePopup) {
    this.id = 'vehicle-custom-layer';
    this.type = 'custom';
    this.renderingMode = '2d';
    this.vehicleManager = vehicleManager;
    this.updatePopup = updatePopup;
    this.clickedVehicle = undefined;

    // Initialiser les sources des shaders
    this.vertexShaderSource = `
          precision highp float;

          // Attributs d'entrée pour chaque vertex
          attribute vec2  a_position;            // Position du vertex dans le quad (-1 à +1)
          attribute vec2  a_texCoord;           // Coordonnées de texture génériques
          attribute vec2  a_vehicleTextureCoord; // Coordonnées de texture pour l'icône du véhicule
          attribute vec2  a_charCoord1;         // Coordonnées de texture pour le premier caractère
          attribute vec2  a_charCoord2;         // Coordonnées de texture pour le deuxième caractère
          attribute vec2  a_atlasConfig;        // Configuration de l'atlas (cols, rows)
          attribute float a_vehicleType;        // Type de véhicule : bus, tram, voiture, etc.
          attribute vec4  a_paintZone;          // Zone de dessin du texte

          // Positions des coins pour chaque instance (décalage haute précision)
          attribute vec4 a_cornerPosition0;
          attribute vec4 a_cornerPosition1;
          attribute vec4 a_cornerPosition2;
          attribute vec4 a_cornerPosition3;

          // Uniformes pour la position de la caméra et la projection
          uniform vec4 u_eye_high;
          uniform vec4 u_eye_low;
          uniform mat4 u_matrix;

          // Variables passant au shader fragment
          varying vec2  v_vehicleCoord; 
          varying vec2  v_texCoord;
          varying vec2  v_charCoord1; 
          varying vec2  v_charCoord2; 
          varying float v_vehicleType;
          varying vec4  v_paintZone;

          void main() {
            //-----------------------------------------
            // 1. Sélection du coin par interpolation
            //-----------------------------------------
            // On transforme a_position de [-1..1] en [0..1]
            // afin de faire une interpolation bilinéaire 
            // entre les 4 coins (bottom-left, bottom-right, top-left, top-right).
            vec2 uv = (a_position + 1.0) * 0.5;

            // Interpolation horizontale (entre coin gauche et coin droit)
            // Pour la "ligne" du bas (bottom) et pour la "ligne" du haut (top).
            vec4 bottomCorner = mix(a_cornerPosition0, a_cornerPosition1, uv.x);
            vec4 topCorner    = mix(a_cornerPosition2, a_cornerPosition3, uv.x);

            // Interpolation verticale (entre bottom et top)
            vec4 instancePosition = mix(bottomCorner, topCorner, uv.y);

            //------------------------------------------------------
            // 2. Calcul de la position finale en soustrayant la cam
            //------------------------------------------------------
            // Séparation haute/basse précision (technique "double precision" simulée en WebGL)
            // On soustrait d'abord u_eye_high, puis u_eye_low.
            gl_Position = vec4(
              vec3(instancePosition.rg, 0.0) - u_eye_high.xyz,
              0.0
            );
            gl_Position += vec4(
              vec3(instancePosition.ba, 0.0) - u_eye_low.xyz,
              0.0
            );

            //-------------------------------------
            // 3. Application de la matrice globale
            //-------------------------------------
            gl_Position = u_matrix * gl_Position;

            //---------------------------------------------------------
            // 4. Correction pour la projection (dépend de votre setup)
            //---------------------------------------------------------
            // Ex. : on ajoute u_eye_high.w dans gl_Position.w 
            // si celui-ci contient, par exemple, la distance ou l'offset
            // nécessaire à la projection. 
            gl_Position.w += u_eye_high.w;

            // Taille de point (si vous utilisez un rendu "point" ou un fallback)
            gl_PointSize = 20.0;

            //----------------------------------------------------
            // 5. Transmission des variables au shader fragment
            //----------------------------------------------------
            // a_texCoord : (0..1) sur le quad
            // a_vehicleTextureCoord : position de base dans l'atlas du véhicule
            // a_atlasConfig : (cols, rows) => pour diviser / indexer la bonne tuile
            v_texCoord     = a_texCoord;
            v_vehicleCoord = a_vehicleTextureCoord + (a_texCoord / a_atlasConfig);

            // Caractères et infos associées
            v_charCoord1   = a_charCoord1;
            v_charCoord2   = a_charCoord2;
            v_vehicleType  = a_vehicleType;
            v_paintZone    = a_paintZone;
          }

    `;

    this.fragmentShaderSource = `
          precision highp float;

          // Uniformes pour les textures
          uniform sampler2D u_textureCars;    // Texture pour les icônes de véhicules (voitures)
          uniform sampler2D u_textureBuses;   // Texture pour les icônes de bus
          uniform sampler2D u_textureTrams;   // Texture pour les icônes de trams
          uniform sampler2D u_textureLetters; // Texture pour les lettres

          // Variables reçues du shader vertex
          varying vec2  v_vehicleCoord; // Coordonnées de texture pour l'icône du véhicule
          varying vec2  v_texCoord;     // Coordonnées de texture génériques
          varying vec2  v_charCoord1;   // Coordonnées de texture pour le premier caractère
          varying vec2  v_charCoord2;   // Coordonnées de texture pour le second caractère
          varying float v_vehicleType;  // Type de véhicule : 0 = bus, ~1 = tram, >=2 = voiture
          varying vec4  v_paintZone;    // (s, t, p, q) limites de la zone où peindre les caractères

          void main() {
            // --- 1. Détermination de la couleur de base du véhicule ---
            // Utilisation de seuils pour déterminer le type de véhicule,
            // tout en évitant de multiplier les "if" stricts. 
            // Selon vos besoins exacts, vous pouvez conserver vos conditions 
            // (v_vehicleType == 0, etc.). 

            vec4 vehicleColor;
            if      (v_vehicleType < 0.5) { // Bus
              vehicleColor = texture2D(u_textureBuses, v_vehicleCoord);
            } 
            else if (v_vehicleType < 1.5) { // Tram
              vehicleColor = texture2D(u_textureTrams, v_vehicleCoord);
            } 
            else {                         // Car
              vehicleColor = texture2D(u_textureCars, v_vehicleCoord);
            }

            // --- 2. Vérification de la zone de peinture et du type de véhicule ---
            // Si on est hors zone ou si c'est un véhicule "car" (v_vehicleType >= 2) 
            // qui ne nécessite pas de lettres, on sort rapidement.
            bool canPaintLetters = (v_vehicleType < 2.0);
            bool inPaintZone     = (v_texCoord.x >= v_paintZone.s && v_texCoord.x <= v_paintZone.t &&
                                    v_texCoord.y >= v_paintZone.p && v_texCoord.y <= v_paintZone.q);

            if (!canPaintLetters || !inPaintZone) {
              // Dans ce cas, on affiche seulement l'icône du véhicule
              gl_FragColor = vehicleColor;
              return;
            }

            vehicleColor = vec4(1.0, 1.0, 1.0, 1.0);

            // --- 3. Calcul des coordonnées de texture *relatives* dans la zone de peinture ---
            float w = v_paintZone.t - v_paintZone.s;
            float h = v_paintZone.q - v_paintZone.p;

            // On "normalise" v_texCoord dans [0,1] sur la zone
            vec2 zoneTexCoord = (v_texCoord - vec2(v_paintZone.s, v_paintZone.p)) / vec2(w, h);
            // Inversion de l'axe Y si nécessaire (pour que 0 soit en bas, etc.)
            zoneTexCoord.y = 1.0 - zoneTexCoord.y;

            // --- 4. Gestion du nombre de caractères à peindre ---
            bool singleChar = (v_charCoord2.x == 0.0 && v_charCoord2.y == 0.0);

            // --- 5. Peinture du ou des caractères ---
            //    5a) S'il n'y a qu'un seul caractère, on le centre entre 0.25 et 0.75.
            if (singleChar) {
              if (zoneTexCoord.x > 0.25 && zoneTexCoord.x < 0.75) {
                // Recadrage pour la zone du 1er caractère (centrée)
                vec2 charTexCoord = vec2((zoneTexCoord.x - 0.25) * 2.0, zoneTexCoord.y);
                // Notre fontsheet est divisée en "cases" 6x6 (d'après votre code)
                vec2 charCoord = v_charCoord1 + (charTexCoord / vec2(6.0, 6.0));
                vec4 charColor = texture2D(u_textureLetters, charCoord);

                // On mélange la couleur de l'icône et celle du texte.
                gl_FragColor = mix(vehicleColor, charColor, charColor.a);
              } else {
                // En dehors de la zone centrale pour le caractère => on garde juste le véhicule
                gl_FragColor = vehicleColor;
              }
              return;
            }

            //    5b) S'il y a deux caractères, on coupe la zone en deux sur l'axe X (0.0-0.5) / (0.5-1.0).
            if (zoneTexCoord.x < 0.5) {
              // Premier caractère (à gauche)
              vec2 charTexCoord = vec2(zoneTexCoord.x * 2.0, zoneTexCoord.y);
              vec2 charCoord    = v_charCoord1 + (charTexCoord / vec2(6.0, 6.0));
              vec4 charColor    = texture2D(u_textureLetters, charCoord);

              gl_FragColor = mix(vehicleColor, charColor, charColor.a);
            } else {
              // Deuxième caractère (à droite)
              vec2 charTexCoord = vec2((zoneTexCoord.x - 0.5) * 2.0, zoneTexCoord.y);
              vec2 charCoord    = v_charCoord2 + (charTexCoord / vec2(6.0, 6.0));
              vec4 charColor    = texture2D(u_textureLetters, charCoord);

              gl_FragColor = mix(vehicleColor, charColor, charColor.a);
            }
            // Antialiasing simple basé sur l'alpha
            float alpha = smoothstep(0.1, 0.2, gl_FragColor.a);

            gl_FragColor = vec4(gl_FragColor.rgb, alpha);
          }
    `;
  }

  onAdd(map: maplibregl.Map, gl: WebGLRenderingContext) {
    this.map = map;
    this.gl = gl as WebGL2RenderingContext;

    // Initialiser les shaders, les programmes et les buffers
    try {
      this.initShaders();
      this.initBuffers();
      this.loadTextures();
    } catch (e) {
      console.error(e);
      throw e;
    }

    this.ready = true;

    // Utilisation des événements émis par MapLibre pour gérer la perte/restauration du contexte
    this.map.on('webglcontextlost', this.handleContextLost);
    this.map.on('webglcontextrestored', this.handleContextRestored);

    // Gestion des mouvements de la caméra
    this.map.on('move', () => {
      this.cameraMoved = true;
    });
    this.map.on('zoom', () => {
      this.cameraMoved = true;
    });
    this.map.on('rotate', () => {
      this.cameraMoved = true;
    });
    this.map.on('pitch', () => {
      this.cameraMoved = true;
    });

    this.map.on('mousemove', (e) => {
      if (this.hitVehicle(e.lngLat) !== undefined) {
        // Changer le curseur lorsque la souris survole la couche
        this.map.getCanvas().style.cursor = 'pointer';
      } else {
        // Rétablir le curseur par défaut
        this.map.getCanvas().style.cursor = 'grab';
      }
    });

    // Gestionnaire d'événements pour le clic sur la carte
    this.map.on('click', (e) => {
      console.log('click', e.lngLat);
      const clickLngLat = e.lngLat; // Position du clic en coordonnées géographiques

      // Seuil de proximité pour détecter un clic sur un véhicule
      this.clickedVehicle = this.hitVehicle(clickLngLat);

      if (this.clickedVehicle !== undefined) {
        const vehicle = this.clickedVehicle;
        const currentStopSequence = vehicle.getNextStopSequence(Date.now());
        if (currentStopSequence !== undefined) {
          this.popupContent = {
            arrival_time: '',
            delay: '',
            isOffline: false,
            label: '',
            stop_name: '',
            type: VehicleType.Bus,
            trip_headsign: ''
          }
          tripService.nextStop(vehicle.id, currentStopSequence).then((stops) => {
            if (stops.length > 0) {
              const stop = stops[0];
              const delay = stop.delay !== undefined ? `(${secondsToMinutes(stop.delay)} min)` : '';
              this.popupContent = {
                type: vehicle.type,
                arrival_time: stop.arrival_time,
                delay,
                isOffline: stop.isOffline,
                label: vehicle.label,
                stop_name: stop.stop_name,
                trip_headsign: stop.trip_headsign
              }
            }
          }).catch(err => {
            this.popupContent = err;
          });
        }
      }
    });
    this.map.on('dblclick', (e) => {
      if (!import.meta.env.DEV) return;
      e.preventDefault();
      const clickLngLat = e.lngLat; // Position du clic en coordonnées géographiques

      // Seuil de proximité pour détecter un clic sur un véhicule
      this.clickedVehicle = this.hitVehicle(clickLngLat);

      if (this.clickedVehicle !== undefined) {
        this.vehicleManager.visualizeTrips(this.clickedVehicle);
      }
    });
  }

  onRemove(map: maplibregl.Map, gl: WebGLRenderingContext) {
    // Retirer les écouteurs d'événements sur la carte lors du retrait de la couche
    this.map.off('webglcontextlost', this.handleContextLost);
    this.map.off('webglcontextrestored', this.handleContextRestored);
  }

  /**
   * Detect si un véhicule est sous la souris
   * @param clickLngLat la position de la souris en longitude et latitude
   * @returns le véhicule sous la souris ou undefined
   */
  private hitVehicle(clickLngLat: maplibregl.LngLat): Vehicle | undefined {
    const threshold = 0.00015; // Ajustez cette valeur si nécessaire

    let minDistance = Infinity;

    let hitVehicle;

    this.vehicles.forEach((vehicle) => {
      const position = vehicle.getPositionAt(Date.now());
      if (position !== null) {
        const dx = position.latitude - clickLngLat.lat;
        const dy = position.longitude - clickLngLat.lng;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // console.log(distance);
        if (distance < threshold && distance < minDistance) {
          minDistance = distance;
          hitVehicle = vehicle;
        }
      } else {
        this.vehicleManager.removeVehicle(vehicle.id);
        // this.refreshData();
      }
    });
    return hitVehicle;
  }

  render(gl: WebGLRenderingContext, args: any) {
    const matrix: number[] = args.defaultProjectionData.mainMatrix;
    if (this.cameraMoved) {
      const eye = this.calculateEyePosition(matrix);
      this.eye_high = eye.high;
      this.eye_low = eye.low;
      this.cameraMoved = false;
    }

    this.drawVehicles(matrix);

    if (this.clickedVehicle !== undefined) {
      const position = this.clickedVehicle.getPositionAt(Date.now());
      if (position != null && this.popupContent !== undefined) {
        this.updatePopup(true, [position.longitude, position.latitude], this.popupContent);
      }
    } else {
      this.updatePopup(false, [0, 0]);
    }
  }

  private initShaders() {
    const gl = this.gl;

    // Compiler les shaders
    const vertexShader = this.compileShader(gl.VERTEX_SHADER, this.vertexShaderSource);
    const fragmentShader = this.compileShader(gl.FRAGMENT_SHADER, this.fragmentShaderSource);

    // Créer le programme et lier les shaders
    const program = gl.createProgram();
    if (program == null) {
      throw new Error('Impossible de créer le programme WebGL');
    }
    this.program = program;

    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    // Vérifier le statut du linkage
    if (gl.getProgramParameter(this.program, gl.LINK_STATUS) === false) {
      const info = gl.getProgramInfoLog(this.program);
      throw new Error(`Erreur lors du linkage du programme : ${info}`);
    }
  }

  /**
   * Fonction pour formater les erreurs de shader pour une meilleure lisibilité
   */
  private formatShaderError(errorLog: string, source: string) {
    const lines = source.split('\n');
    const formattedErrorLog = errorLog
      .split('\n')
      .map((errorLine) => {
        const match = errorLine.match(/ERROR: \d+:(\d+): (.+)/);
        if (match !== null) {
          const lineNumber = parseInt(match[1], 10);
          const errorMessage = match[2];
          const sourceLine = lines[lineNumber - 1].trim();
          return `Ligne ${lineNumber}: ${errorMessage}\nCode source: ${sourceLine}`;
        }
        return errorLine;
      })
      .join('\n');
    return formattedErrorLog;
  }

  private compileShader(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (shader == null) {
      throw new Error('Impossible de créer le shader');
    }
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    // Vérifier la compilation
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS) === false) {
      const errorLog = gl.getShaderInfoLog(shader);
      let error;
      if (errorLog !== null) {
        error = `Erreur de compilation du shader ${type === gl.VERTEX_SHADER ? 'vertex' : 'fragment'}:\n${this.formatShaderError(errorLog, source)}`;
      } else {
        error = `Erreur de compilation du shader \n${source}`;
      }
      gl.deleteShader(shader);
      throw new Error(error);
    }
    return shader;
  }

  private initBuffers() {
    const gl = this.gl;

    // Buffer pour les positions des sommets du quad
    const positionBuffer = gl.createBuffer();
    if (positionBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les positions');
    }
    this.positionBuffer = positionBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    const positions = new Float32Array([
      -1.0,
      -1.0, // Coin inférieur gauche
      1.0,
      -1.0, // Coin inférieur droit
      -1.0,
      1.0, // Coin supérieur gauche
      1.0,
      1.0 // Coin supérieur droit
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Buffer pour les coordonnées de texture
    const texCoordBuffer = gl.createBuffer();
    if (texCoordBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les coordonnées de texture');
    }
    this.texCoordBuffer = texCoordBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    const texCoords = new Float32Array([
      0.0,
      0.0, // bas gauche
      1.0,
      0.0, // bas droite
      0.0,
      1.0, // haut gauche
      1.0,
      1.0 // haut droite
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);

    // Initialiser les données des véhicules
    this.initializeVehicleData();

    // Buffer pour les positions des coins des instances
    const instanceCornerPositionBuffer = gl.createBuffer();
    if (instanceCornerPositionBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les positions des coins des instances');
    }
    this.instanceCornerPositionBuffer = instanceCornerPositionBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.instanceCornerPositions, gl.DYNAMIC_DRAW);

    // Buffer pour les types de véhicules
    const typeBuffer = gl.createBuffer();
    if (typeBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les types de véhicules');
    }
    this.typeBuffer = typeBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.typeBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.vehicleTypes, gl.STATIC_DRAW);

    // Buffer pour les coordonnées de texture des véhicules
    const vehicleTextureCoordBuffer = gl.createBuffer();
    if (vehicleTextureCoordBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les coordonnées de texture des véhicules');
    }
    this.vehicleTextureCoordBuffer = vehicleTextureCoordBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vehicleTextureCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.vehicleTextureCoords, gl.STATIC_DRAW);

    // Buffer pour les coordonnées de texture des caractères
    const charCoordBuffer1 = gl.createBuffer();
    if (charCoordBuffer1 == null) {
      throw new Error('Impossible de créer le buffer pour les coordonnées de texture des caractères (1)');
    }
    this.charCoordBuffer1 = charCoordBuffer1;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer1);
    gl.bufferData(gl.ARRAY_BUFFER, this.charTextureCoords1, gl.STATIC_DRAW);

    const charCoordBuffer2 = gl.createBuffer();
    if (charCoordBuffer2 == null) {
      throw new Error('Impossible de créer le buffer pour les coordonnées de texture des caractères (2)');
    }
    this.charCoordBuffer2 = charCoordBuffer2;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer2);
    gl.bufferData(gl.ARRAY_BUFFER, this.charTextureCoords2, gl.STATIC_DRAW);

    // Buffer pour les configurations des atlas
    const atlasConfigBuffer = gl.createBuffer();
    if (atlasConfigBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les configurations des atlas');
    }
    this.atlasConfigBuffer = atlasConfigBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.atlasConfigBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.atlasConfs, gl.STATIC_DRAW);

    // Buffer pour les zones de peinture
    const paintZoneBuffer = gl.createBuffer();
    if (paintZoneBuffer == null) {
      throw new Error('Impossible de créer le buffer pour les zones de peinture');
    }
    this.paintZoneBuffer = paintZoneBuffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.paintZoneBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.paintZones, gl.STATIC_DRAW);
  }

  private loadTextures() {
    const gl = this.gl;

    // Charger les images des atlas
    const loadImage = async (url: string): Promise<HTMLImageElement> => {
      return await new Promise((resolve, reject) => {
        const image = new Image();
        image.src = url;
        image.onload = () => {
          resolve(image);
        };
        image.onerror = (err) => {
          reject(err);
        };
      });
    };

    Promise.all([
      loadImage('./assets/textures/texture_atlas_cars.png'),
      loadImage('./assets/textures/texture_atlas_bus.png'),
      loadImage('./assets/textures/texture_atlas_tram.png'),
      loadImage('./assets/textures/texture_atlas_letters.png')
    ])
      .then(([carImage, busImage, tramImage, letterImage]) => {
        // Créer et configurer les textures
        const textureCars = gl.createTexture();
        if (textureCars == null) {
          throw new Error('Impossible de créer la texture pour les voitures');
        }
        this.textureCars = textureCars;
        this.setupTexture(gl, carImage, this.textureCars, this.textureUnitCars, false);

        const textureBuses = gl.createTexture();
        if (textureBuses == null) {
          throw new Error('Impossible de créer la texture pour les bus');
        }
        this.textureBuses = textureBuses;
        this.setupTexture(gl, busImage, this.textureBuses, this.textureUnitBuses, false);

        const textureTrams = gl.createTexture();
        if (textureTrams == null) {
          throw new Error('Impossible de créer la texture pour les trams');
        }
        this.textureTrams = textureTrams;
        this.setupTexture(gl, tramImage, this.textureTrams, this.textureUnitTrams, false);

        const textureLetters = gl.createTexture();
        if (textureLetters == null) {
          throw new Error('Impossible de créer la texture pour les lettres');
        }
        this.textureLetters = textureLetters;
        this.setupTexture(gl, letterImage, this.textureLetters, this.textureUnitLetters, true);
      })
      .catch((err) => {
        console.error('Erreur lors du chargement des textures :', err);
      });
  }

  private setupTexture(gl: WebGL2RenderingContext, image: HTMLImageElement, texture: WebGLTexture, textureUnit: number, precision: boolean) {
    gl.activeTexture(gl.TEXTURE0 + textureUnit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    // if (precision) {
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    // } else {
    //   gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    //   gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    //   gl.generateMipmap(gl.TEXTURE_2D);
    // }
  }

  private calculateEyePosition(matrix: number[]) {
    // Calculer u_eye_high et u_eye_low
    const mat: number[][] = [
      [matrix[0], matrix[4], matrix[8], matrix[12]],
      [matrix[1], matrix[5], matrix[9], matrix[13]],
      [matrix[2], matrix[6], matrix[10], matrix[14]],
      [matrix[3], matrix[7], matrix[11], matrix[15]]
    ];

    const eye = math.lusolve(mat, [[0], [0], [0], [1]]) as number[][];

    const clipW = 1.0 / eye[3][0];
    const eyeDivided = math.divide(eye, eye[3][0]) as number[];
    const eyeFlat = math.flatten(eyeDivided);

    eyeFlat[3] = clipW;

    const high = new Float32Array(eyeFlat.map((e: number) => Math.fround(e)));
    const low = new Float32Array(eyeFlat.map((e: number, i: number) => e - high[i]));

    return { high, low };
  }

  private initializeVehicleData() {
    console.log('InitializeVehicleData');
    // Initialiser les données des véhicules
    const vehicles = this.vehicleManager.getAllVehicles();
    this.vehicles = vehicles;
    const vehicleCount = vehicles.length;

    // Initialiser les tableaux
    this.instanceCornerPositions = new Float32Array(vehicleCount * 16);
    this.vehicleTypes = new Float32Array(vehicleCount);
    this.vehicleTextureCoords = new Float32Array(vehicleCount * 2);
    this.charTextureCoords1 = new Float32Array(vehicleCount * 2);
    this.charTextureCoords2 = new Float32Array(vehicleCount * 2);
    this.paintZones = new Float32Array(vehicleCount * 4);
    this.atlasConfs = new Float32Array(vehicleCount * 2);

    // Configurations des atlas
    const atlasConfigs: Record<string, AtlasConfig> = {
      car: {
        src: '/textures/texture_atlas_cars.png',
        cols: 10,
        rows: 5,
        width: 1280,
        height: 640,
        colors: generateRainbowColors(10 * 5),
        paintWindow: [0, 0, 1, 1]
      },
      bus: {
        src: '/textures/texture_atlas_bus.png',
        cols: 10,
        rows: 5,
        width: 1280,
        height: 640,
        colors: generateRainbowColors(10 * 5),
        paintWindow: [0.28, 0.63, 0.4, 0.6]
      },
      tram: {
        src: '/textures/texture_atlas_tram.png',
        cols: 10,
        rows: 5,
        width: 1280,
        height: 640,
        colors: generateRainbowColors(10 * 5),
        paintWindow: [0.50, 0.76, 0.42, 0.58]
      },
      letter: {
        src: '/textures/texture_atlas_letters.png',
        cols: 6,
        rows: 6,
        width: 144,
        height: 150,
        colors: [],
        paintWindow: [0, 0, 1, 1]
      }
    };

    const charAtlasConfig = atlasConfigs.letter;
    const charSingleTextureWidth = charAtlasConfig.width / charAtlasConfig.cols;
    const charSingleTextureHeight = charAtlasConfig.height / charAtlasConfig.rows;

    // Alphabet utilisé pour les textures de lettres
    const alphabet = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9'
    ];

    // Initialiser les données pour chaque véhicule
    vehicles.forEach((vehicle, i) => {
      const atlasConfig = atlasConfigs[vehicle.type];

      // Obtenir la couleur du véhicule
      const vehicleColor = vehicle.color; // Assurez-vous que vehicle.color est de type { r: number; g: number; b: number }

      // Trouver la couleur la plus proche dans l'atlas
      const { row, col } = findClosestColorInAtlas(vehicleColor, atlasConfig);

      const singleTextureWidth = atlasConfig.width / atlasConfig.cols;
      const singleTextureHeight = atlasConfig.height / atlasConfig.rows;

      this.vehicleTextureCoords[i * 2] = (col * singleTextureWidth) / atlasConfig.width;
      this.vehicleTextureCoords[i * 2 + 1] = (row * singleTextureHeight) / atlasConfig.height;

      // Récupération des caractères pour l'étiquette du véhicule
      const label = vehicle.label;
      const char1 = label[0] ?? ' ';
      const char2 = label[1] ?? ' ';

      // Calcul des indices des caractères dans l'alphabet
      const charIndex1 = alphabet.indexOf(char1.toUpperCase());
      const charIndex2 = alphabet.indexOf(char2.toUpperCase());

      const validCharIndex1 = charIndex1 !== -1 ? charIndex1 : 0; // Utilisation de l'espace si non trouvé
      const validCharIndex2 = charIndex2 !== -1 ? charIndex2 : 0;

      // Calcul des coordonnées de texture pour le premier caractère
      const charRow1 = Math.floor(validCharIndex1 / charAtlasConfig.cols);
      const charCol1 = validCharIndex1 % charAtlasConfig.cols;

      this.charTextureCoords1[i * 2] = (charCol1 * charSingleTextureWidth) / charAtlasConfig.width;
      this.charTextureCoords1[i * 2 + 1] = (charRow1 * charSingleTextureHeight) / charAtlasConfig.height;

      // Calcul des coordonnées de texture pour le deuxième caractère
      const charRow2 = Math.floor(validCharIndex2 / charAtlasConfig.cols);
      const charCol2 = validCharIndex2 % charAtlasConfig.cols;

      this.charTextureCoords2[i * 2] = (charCol2 * charSingleTextureWidth) / charAtlasConfig.width;
      this.charTextureCoords2[i * 2 + 1] = (charRow2 * charSingleTextureHeight) / charAtlasConfig.height;

      // Type de véhicule
      if (vehicle.type === VehicleType.Bus) {
        this.vehicleTypes[i] = 0.0;
      } else if (vehicle.type === VehicleType.Tram) {
        this.vehicleTypes[i] = 1.0;
      } else if (vehicle.type === VehicleType.Car) {
        this.vehicleTypes[i] = 2.0;
      }

      // Zones de peinture
      this.paintZones.set(atlasConfig.paintWindow, i * 4);

      // Configurations des atlas
      this.atlasConfs[i * 2] = atlasConfig.cols;
      this.atlasConfs[i * 2 + 1] = atlasConfig.rows;
    });
  }

  // Méthode publique pour forcer le rafraîchissement des données depuis le VehicleManager
  public refreshData() {
    const gl = this.gl;

    if (!this.ready) return;

    // Ré-initialiser les données des véhicules
    this.initializeVehicleData();

    // Mettre à jour les buffers GPU en fonction des nouveaux tableaux
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.instanceCornerPositions, gl.DYNAMIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.typeBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.vehicleTypes, gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.vehicleTextureCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.vehicleTextureCoords, gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer1);
    gl.bufferData(gl.ARRAY_BUFFER, this.charTextureCoords1, gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer2);
    gl.bufferData(gl.ARRAY_BUFFER, this.charTextureCoords2, gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.atlasConfigBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.atlasConfs, gl.STATIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.paintZoneBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.paintZones, gl.STATIC_DRAW);

    // Demander un repaint de la carte
    this.map.triggerRepaint();
    console.log('Paint');
  }

  updateVehiclePositions() {
    if (!this.ready) return;
    const gl = this.gl;
    const currentTime = Date.now();

    this.vehicles.forEach((vehicle, i) => {
      const position = vehicle.getPositionAt(currentTime);
      if (position != null && !Number.isNaN(position.longitude) && !Number.isNaN(position.latitude)) {
        // Convertir la position en MercatorCoordinate
        const mercatorCoord = maplibregl.MercatorCoordinate.fromLngLat([position.longitude, position.latitude]);

        // Ajustement de l'angle pour corriger la rotation si nécessaire
        const angle = (position.bearing ?? 0) * (Math.PI / 180) + Math.PI;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        const size = this.primaryWidthDegrees(this.map.getZoom()) / 600; // Taille de l'icône (ajustez selon vos besoins)

        // Calcul des positions des coins avec la rotation
        const x: number = mercatorCoord.x;
        const y: number = mercatorCoord.y;

        const corners = [
          [x + (-size * cosA + -size * sinA), y + (-size * sinA - -size * cosA)],
          [x + (size * cosA + -size * sinA), y + (size * sinA - -size * cosA)],
          [x + (-size * cosA + size * sinA), y + (-size * sinA - size * cosA)],
          [x + (size * cosA + size * sinA), y + (size * sinA - size * cosA)]
        ];

        for (let j = 0; j < 4; j++) {
          this.instanceCornerPositions[i * 16 + j * 4] = corners[j][0];
          this.instanceCornerPositions[i * 16 + j * 4 + 1] = corners[j][1];
          this.instanceCornerPositions[i * 16 + j * 4 + 2] = corners[j][0] - Math.fround(corners[j][0]);
          this.instanceCornerPositions[i * 16 + j * 4 + 3] = corners[j][1] - Math.fround(corners[j][1]);
        }
      }
    });

    // Mise à jour du buffer avec les nouvelles positions
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.instanceCornerPositions);
  }

  // Palette de largeurs (approx.) observées dans le CartoCSS
  // pour highway=primary, en pixels, selon le zoom entier.
  // z < 8 => pas de primary dans la feuille de style standard
  // (on pourrait laisser 0 px ou ne pas l'afficher).
  primaryWidths = new Map<number, number>([
    // -- Low zoom / roads-low-zoom --
    [8, 1.0], // @primary-width-z8 = 1
    [9, 1.4], // @primary-width-z9 = 1.4
    [10, 1.8], // @primary-width-z10= 1.8
    [11, 1.8], // parfois stable entre z=10 et z=11
    // -- Normal zoom / roads-fill --
    [12, 3.5], // @primary-width-z12= 3.5
    [13, 5.0], // @primary-width-z13= 5
    [14, 5.0], // (pas explicitement défini dans la feuille, on met 5)
    [15, 10.0], // @primary-width-z15= 10
    [16, 10.0], // (idem)
    [17, 18.0], // @primary-width-z17= 18
    [18, 21.0], // @primary-width-z18= 21
    [19, 27.0], // @primary-width-z19= 27
    [20, 33.0] // @primary-width-z20= 33
  ]);

  /**
 * Calcule la largeur en degrés (à l'équateur) d'une route highway=primary
 * en fonction d'un zoom décimal, en se basant sur la feuille de style standard OSM.
 *
 * - Pour z < 12, on récupère les largeurs “low zoom” (#roads-low-zoom).
 * - À partir de z = 12, on prend les largeurs “normales” (#roads-fill).
 * - On fait une interpolation linéaire entre les paliers entiers.
 * - En deçà de z=8, la route primary n’apparaît généralement pas => on force à 0 px.
 */
  private primaryWidthDegrees(zoom: number): number {
    // 1) Gestion des bornes min/max
    //    En dessous de 8, la route "primary" n’est pas censée s’afficher => 0 px
    if (zoom < 8) {
      return 0;
    }
    // Au-dessus de 20, on plafonne à 20 (ou on extrapole)
    if (zoom > 20) {
      zoom = 20;
    }

    // 2) Zoom floor/ceil
    const zFloor = Math.floor(zoom);
    const zCeil = Math.ceil(zoom);

    // Cas où zoom est un entier
    if (zFloor === zCeil) {
      const wpx = this.primaryWidths.get(zFloor) ?? 0;
      return wpx * this.degreesPerPixel(zoom);
    }

    // 3) Sinon interpolation linéaire
    //    Ex: si zoom=10.3 => entre wFloor=1.8(px) et wCeil=1.8(px)
    const wFloor = this.primaryWidths.get(zFloor) ?? 0;
    const wCeil = this.primaryWidths.get(zCeil) ?? 0;
    const ratio = zoom - zFloor; // ex: 10.3 - 10 = 0.3
    const wpx = wFloor + (wCeil - wFloor) * ratio;

    // 4) Conversion px -> degrés
    return wpx * this.degreesPerPixel(zoom);
  }

  /**
   * Retourne le nombre de degrés (à l'équateur)
   * par pixel pour un zoom décimal donné (projection WebMercator).
   */
  private degreesPerPixel(z: number): number {
    // 360° / (256 px * 2^z)
    return 360.0 / (256.0 * Math.pow(2, z));
  }

  private drawVehicles(matrix: number[]) {
    const gl = this.gl;

    gl.useProgram(this.program);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    // Envoi des uniformes au shader
    gl.uniform4fv(gl.getUniformLocation(this.program, 'u_eye_high'), this.eye_high);
    gl.uniform4fv(gl.getUniformLocation(this.program, 'u_eye_low'), this.eye_low);

    // Envoi de la matrice de transformation
    gl.uniformMatrix4fv(gl.getUniformLocation(this.program, 'u_matrix'), false, new Float32Array(matrix));

    // Envoi des textures
    gl.activeTexture(gl.TEXTURE0 + this.textureUnitCars);
    gl.bindTexture(gl.TEXTURE_2D, this.textureCars);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureCars'), this.textureUnitCars);

    gl.activeTexture(gl.TEXTURE0 + this.textureUnitBuses);
    gl.bindTexture(gl.TEXTURE_2D, this.textureBuses);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureBuses'), this.textureUnitBuses);

    gl.activeTexture(gl.TEXTURE0 + this.textureUnitTrams);
    gl.bindTexture(gl.TEXTURE_2D, this.textureTrams);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureTrams'), this.textureUnitTrams);

    gl.activeTexture(gl.TEXTURE0 + this.textureUnitLetters);
    gl.bindTexture(gl.TEXTURE_2D, this.textureLetters);
    gl.uniform1i(gl.getUniformLocation(this.program, 'u_textureLetters'), this.textureUnitLetters);

    // Configurer les attributs
    // a_position
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    const aPosition = gl.getAttribLocation(this.program, 'a_position');
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    // a_texCoord
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    const aTexCoord = gl.getAttribLocation(this.program, 'a_texCoord');
    gl.enableVertexAttribArray(aTexCoord);
    gl.vertexAttribPointer(aTexCoord, 2, gl.FLOAT, false, 0, 0);

    // a_vehicleTextureCoord
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vehicleTextureCoordBuffer);
    const aVehicleTextureCoord = gl.getAttribLocation(this.program, 'a_vehicleTextureCoord');
    gl.enableVertexAttribArray(aVehicleTextureCoord);
    gl.vertexAttribPointer(aVehicleTextureCoord, 2, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aVehicleTextureCoord, 1);

    // a_charCoord1
    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer1);
    const aCharCoord1 = gl.getAttribLocation(this.program, 'a_charCoord1');
    gl.enableVertexAttribArray(aCharCoord1);
    gl.vertexAttribPointer(aCharCoord1, 2, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aCharCoord1, 1);

    // a_charCoord2
    gl.bindBuffer(gl.ARRAY_BUFFER, this.charCoordBuffer2);
    const aCharCoord2 = gl.getAttribLocation(this.program, 'a_charCoord2');
    gl.enableVertexAttribArray(aCharCoord2);
    gl.vertexAttribPointer(aCharCoord2, 2, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aCharCoord2, 1);

    // a_atlasConfig
    gl.bindBuffer(gl.ARRAY_BUFFER, this.atlasConfigBuffer);
    const aAtlasConfig = gl.getAttribLocation(this.program, 'a_atlasConfig');
    gl.enableVertexAttribArray(aAtlasConfig);
    gl.vertexAttribPointer(aAtlasConfig, 2, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aAtlasConfig, 1);

    // a_vehicleType
    gl.bindBuffer(gl.ARRAY_BUFFER, this.typeBuffer);
    const aVehicleType = gl.getAttribLocation(this.program, 'a_vehicleType');
    gl.enableVertexAttribArray(aVehicleType);
    gl.vertexAttribPointer(aVehicleType, 1, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aVehicleType, 1);

    // a_paintZone
    gl.bindBuffer(gl.ARRAY_BUFFER, this.paintZoneBuffer);
    const aPaintZone = gl.getAttribLocation(this.program, 'a_paintZone');
    gl.enableVertexAttribArray(aPaintZone);
    gl.vertexAttribPointer(aPaintZone, 4, gl.FLOAT, false, 0, 0);
    (gl as any).vertexAttribDivisor(aPaintZone, 1);

    // a_cornerPosition0, a_cornerPosition1, a_cornerPosition2, a_cornerPosition3
    const aCornerPosition0 = gl.getAttribLocation(this.program, 'a_cornerPosition0');
    const aCornerPosition1 = gl.getAttribLocation(this.program, 'a_cornerPosition1');
    const aCornerPosition2 = gl.getAttribLocation(this.program, 'a_cornerPosition2');
    const aCornerPosition3 = gl.getAttribLocation(this.program, 'a_cornerPosition3');

    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceCornerPositionBuffer);
    gl.enableVertexAttribArray(aCornerPosition0);
    gl.enableVertexAttribArray(aCornerPosition1);
    gl.enableVertexAttribArray(aCornerPosition2);
    gl.enableVertexAttribArray(aCornerPosition3);

    const vec4Size = 4 * Float32Array.BYTES_PER_ELEMENT;
    const instanceStride = 16 * Float32Array.BYTES_PER_ELEMENT;

    gl.vertexAttribPointer(aCornerPosition0, 4, gl.FLOAT, false, instanceStride, 0);
    gl.vertexAttribPointer(aCornerPosition1, 4, gl.FLOAT, false, instanceStride, vec4Size);
    gl.vertexAttribPointer(aCornerPosition2, 4, gl.FLOAT, false, instanceStride, vec4Size * 2);
    gl.vertexAttribPointer(aCornerPosition3, 4, gl.FLOAT, false, instanceStride, vec4Size * 3);

    (gl as any).vertexAttribDivisor(aCornerPosition0, 1);
    (gl as any).vertexAttribDivisor(aCornerPosition1, 1);
    (gl as any).vertexAttribDivisor(aCornerPosition2, 1);
    (gl as any).vertexAttribDivisor(aCornerPosition3, 1);

    // Dessiner les instances
    (gl as any).drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.vehicles.length);
  }
}
